import { Inject } from '@odinjs/odin';
import { DynamicFieldsAPI, FormAPI } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnChange, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { FragmentNames, PseudonymAppointmentsStep_4Aliases } from '../../codegen';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { QuizItems } from '../../interfaces/appointment';
import { ObjectComparatorUtils } from '../../utils/object-comparator-utils';
import PseudonymAppointmentsBaseController from './pseudonym-appointments-base-controller';
import PseudonymAppointmentModel from './pseudonym-appointments-model';
import { QuizMetadata } from './pseudonym-interfaces';
import PseudonymIntegratedScheduleService from './pseudonym-service/pseudonym-integrated-schedule-service';
import { AppointmentsSteps, NO, YES } from './pseudonym-types';

interface QuizAnswer {
  [key: string]: Value;
  label: string;
  value: string;
}

const DefaultQuizParams = {
  READ_ONLY: NO,
  TRANSFER: NO
};

@Controller({ component: PseudonymAppointmentsStep_4Aliases.LAYOUT_QUIZ, fragment: FragmentNames.PSEUDONYM_APPOINTMENTS_STEP_4 })
export default class PseudonymAppointmentsStep4Controller extends PseudonymAppointmentsBaseController {

  @Inject(PseudonymAppointmentsStep_4Aliases.FORM_QUIZ)
    quizFormApi!: FormAPI;

  @Inject(PseudonymAppointmentsStep_4Aliases.DYNAMIC_FIELDS_QUIZ)
    quizDynamicFieldsApi!: DynamicFieldsAPI;

  @Inject('PseudonymIntegratedScheduleService')
    pseudonymIntegratedScheduleService!: PseudonymIntegratedScheduleService;

  @OnMount(PseudonymAppointmentsStep_4Aliases.LAYOUT_QUIZ)
  onMountLayoutQuiz(): void {
    PseudonymAppointmentModel.stepFour.quizFormApi = this.quizFormApi;
    PseudonymAppointmentModel.stepFour.quizDynamicFieldsApi = this.quizDynamicFieldsApi;
    PseudonymAppointmentModel.stepFour.populateQuizTab = () => this.populateQuizTab();
  }

  @OnChange({ target: PseudonymAppointmentsStep_4Aliases.FORM_QUIZ })
  onChangeFormQuiz(): void {
    const answerFormQuiz: Record<string, Value> = this.quizFormApi.getRecord();
    const dynamicMetada = PseudonymAppointmentModel.stepFour.quizDynamicMetada;

    Object.entries(answerFormQuiz).forEach(([key, value]) => {
      const answer = this.formatQuizAnswer(value);
      this.updateQuizAnswer(key, answer, dynamicMetada.id, dynamicMetada.children);
    })
  }

  private formatQuizAnswer(value: Value): QuizAnswer {
    const answer: QuizAnswer = { label: '', value: '' };

    if (ObjectComparatorUtils.isObject(value)) {
      return value as QuizAnswer;
    }

    answer.label = value as string;

    return answer;
  }

  private updateQuizAnswer(key: string, answer: QuizAnswer, fatherId: string, children: Array<QuizMetadata>): void {
    if (children == null || children?.length === 0) {
      return;
    }

    children.forEach((value) => {
      if (value.id === key && value.answer !== answer.label) {
        value.answer = answer.label;
        value.sequenceAnswer = answer.value;
        this.saveQuizItem(fatherId, value);
      }

      this.updateQuizAnswer(key, answer, value.id, value.children);
    });
  }

  private saveQuizItem(seqAgeintItem: string, quizItem: QuizMetadata): void {
    const dynamicMetada = PseudonymAppointmentModel.stepFour.quizDynamicMetada;
    const body = {
      answer: quizItem.answer,
      sequenceAnswer: quizItem.sequenceAnswer !== '' ? Number(quizItem.sequenceAnswer) : null,
      sequenceIntegratedSchedule: Number(dynamicMetada.id),
      sequenceItemIntegratedSchedule: Number(seqAgeintItem),
      sequenceStructureQuestion: Number(quizItem.id)
    };

    HttpService.put(APPLICATION_ENDPOINTS.PUT_QUIZ_ITEM, { nrSeqAgeint: Number(dynamicMetada.id) }, { body })
      .then((response) => {
        const quiz = response.body as Array<{ visible?: boolean }>;
        quiz.forEach((value) => {
          value.visible = String(value.visible) === YES;
        });
        const quizItems = quiz as Array<QuizItems>;
        this.setQuizItemVisibility(quizItems, dynamicMetada.children);
        this.quizDynamicFieldsApi.restoreState(true);
        this.quizDynamicFieldsApi.setMetadata(dynamicMetada);
        this.checkQuizRequiredFields(dynamicMetada.children);
      });
  }

  private setQuizItemVisibility(quizItems: QuizItems[], children: QuizMetadata[]): void {
    if (!quizItems || children == null || children?.length === 0) {
      return;
    }

    children.forEach((value) => {
      quizItems.forEach(item => {
        if (value?.entry && item?.sequenceStructure?.toString() && item?.sequenceStructure?.toString() === value?.entry) {
          value.visibilityCondition = item?.visible;
        }
      });
      this.setQuizItemVisibility(quizItems, value.children);
    });
  }

  private checkQuizRequiredFields(children: Array<QuizMetadata>): void {
    if (children == null || children?.length === 0) {
      return;
    }

    this.setStepQuizDone(true);

    children.forEach(value => {
      if (value.required && !value.answer && value.visibilityCondition) {
        this.setStepQuizDone(false);
      }

      this.checkQuizRequiredFields(value.children);
    });

    this.setWizardStepDone(AppointmentsSteps.STEP_4_QUIZ, this.getStepQuizDone());
  }

  populateQuizTab(): Promise<void> {
    return this.pseudonymIntegratedScheduleService.getIntegratedScheduleSequenceAndSaveInternalProcedures()
      .then(() => this.setQuizMetadata(PseudonymAppointmentModel.integratedScheduleSequence));
  }

  private setQuizMetadata(integratedScheduleSequence: number): Promise<void> {
    if (!integratedScheduleSequence) {
      return Promise.resolve();
    }

    const params = {
      readOnly: DefaultQuizParams.READ_ONLY,
      seqAgeInt: integratedScheduleSequence,
      transfer: DefaultQuizParams.TRANSFER,
    }

    const quizDynamicFields = PseudonymAppointmentModel.stepFour.quizDynamicFieldsApi;
    const quizForm = PseudonymAppointmentModel.stepFour.quizFormApi;
    quizDynamicFields.restoreState(true);
    quizDynamicFields.setMetadata({} as QuizMetadata);
    quizForm.setLoading(true);

    return HttpService.get<QuizMetadata>(APPLICATION_ENDPOINTS.GET_QUIZ_METADATA, params)
      .then(response => {
        const body = response.body;
        const dynamicMetada = body as QuizMetadata;

        if (!dynamicMetada || !dynamicMetada.children) {
          PseudonymAppointmentModel.hasMetadataQuiz = false;
          PseudonymAppointmentModel.stepFour.quizDynamicMetada = {} as QuizMetadata;
          return;
        } if (PseudonymAppointmentModel.stepFour.quizDynamicMetada.id === dynamicMetada.id) {
          quizDynamicFields.setMetadata(PseudonymAppointmentModel.stepFour.quizDynamicMetada);
          this.checkQuizRequiredFields(PseudonymAppointmentModel.stepFour.quizDynamicMetada.children);
          quizForm.setLoading(false);
          return;
        }
        quizDynamicFields.restoreState(true);
        quizDynamicFields.setMetadata(dynamicMetada);
        this.checkQuizRequiredFields(dynamicMetada.children);
        quizForm.setRecord({});
        quizForm.setLoading(false);

        PseudonymAppointmentModel.hasMetadataQuiz = true;
        PseudonymAppointmentModel.stepFour.quizDynamicMetada = dynamicMetada;
      });
  }

  private setStepQuizDone(isDone: boolean): void {
    this.context.set({ stepQuizDone: isDone });
  }

  private getStepQuizDone(): boolean {
    return this.context.get('stepQuizDone') as boolean;
  }

}
